/**
 * Generates HTML email template for "Payment Pre-Authorized" (quote / flight flow).
 * Sent to clients when a pre-authorization hold is placed on their card
 * for a trip booking (funds reserved, captured once the operator confirms flight details).
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { icon, spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../../emailLayout';
import { getAircraftModelName, getOperatorWithLocation, getEffectiveAircraft, resolveAircraftImageUrl } from '@/features/tails/utils';
import { formatUTCToDateTime } from '@/utils/formatters';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatPrice, trunc2 } from '@/utils';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getTripTypeLabel = (legs) => {
  if (!legs || legs.length === 0) return '';
  if (legs.length === 1) return 'One Way';
  if (legs.length === 2) {
    const [a, b] = legs;
    if (a.departure_airport === b.arrival_airport && a.arrival_airport === b.departure_airport) return 'Round Trip';
  }
  return 'Multi-Leg';
};

const getRouteDisplay = (legs) => {
  if (!legs || legs.length === 0) return '';
  if (legs.length === 1) return `${legs[0].departure_airport || ''} → ${legs[0].arrival_airport || ''}`;
  return legs.map((l) => l.departure_airport).concat(legs[legs.length - 1].arrival_airport).filter(Boolean).join(' → ');
};

/**
 * @param {Object} opts
 * @param {Object} opts.quote                - quote record (client, bid_items, passenger_count, quote_number, is_round_trip, is_multi_leg)
 * @param {Object} opts.quote    - accepted bid option (total_price/final_price, federal_excise_tax, segment_tax, operator_cost, card_fee, aircraft, operator)
 * @param {string} [opts.organization]
 * @param {string} [opts.authExpiry]         - formatted authorization expiry (e.g. "Saturday, October 23, 2025 at 14:00 (UTC -05)")
 */
export const buildQuotePaymentConfirmationEmailHtml = async ({
  quote,
  selectedBidOption,
  client,
  paymentMethod,
  card,
  organization
}) => {
  const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ') || 'Valued Client';
  const quoteNumber = quote?.quote_number || quote?.quoteNumber || '';

  const operatorCost = parseFloat(quote?.operator_cost) || 0;
  const brokerMargin = parseFloat(quote?.broker_margin) || 0;
  const platformFee = parseFloat(quote?.platform_fee) || 0;
  const federalExciseTax = parseFloat(quote?.federal_excise_tax) || 0;
  const cardAmount = parseFloat(quote?.card_amount) || 0;
  const segmentTax = parseFloat(quote?.segment_tax) || 0;
  const cardFee = parseFloat(quote?.card_fee) || 0;
  const tripCost = trunc2(operatorCost + brokerMargin);
  const internationalTax = parseFloat(quote?.international_tax) || 0;
  const totalFee = trunc2(federalExciseTax + segmentTax + internationalTax + platformFee);
  const totalCardFee = trunc2(trunc2(tripCost + totalFee) * 0.03);
  const taxesAndFees = trunc2(totalFee + cardFee);
  const totalAmount = quote?.total_due || 0;

  // Default pre-auth hold: 7 days from invoice creation (Visa/MC standard).
  const expiryDate = new Date(new Date(Date.now()).getTime() + 7 * 24 * 60 * 60 * 1000);
    
  const expiryStr = expiryDate.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).replace(/,([^,]*)$/, ' at$1');


  // ── Trip details (same layout as paymentConfirmationEmailTemplate) ──────
  const legs = quote?.bid_items?.[0]?.bid?.leg_itinerary || [];
  const tripType = getTripTypeLabel(legs);
  const routeDisplay = getRouteDisplay(legs);
  const departureDates = legs.map((leg, i) => {
    const tz = getAirportTimezone(leg.departure_airport);
    const date = leg.departure_date ? formatUTCToDateTime(leg.departure_date, tz, 'dddd, MMMM D, YYYY') : '';
    const time = leg.departure_date ? formatUTCToDateTime(leg.departure_date, tz, 'HH:mm') : '';
    return {
      route: `${leg.departure_airport || ''} → ${leg.arrival_airport || ''}`,
      date,
      time,
      label: legs.length > 1 ? `Leg ${i + 1}` : 'Departure Date',
    };
  }).filter((d) => d.date);
  const passengerCount = quote?.passenger_count || quote?.bid_items?.[0]?.bid?.passenger_count || '';

  // ── Aircraft card (same layout as bookingSecuredEmailTemplate) ──────────
  // Aircraft + operator + price come from the accepted bid option, not
  // the quote row (matches the shape the other broker email templates
  // use). Override merge uses the quote's files.aircraft_overrides so
  // broker edits from the pricing drawer surface here too.
  const aircraft = getEffectiveAircraft(selectedBidOption?.aircraft, quote);
  const operator = selectedBidOption?.operator;
  const modelName = getAircraftModelName(selectedBidOption?.aircraft);
  const imageUrl = await resolveAircraftImageUrl(aircraft);
  const priceDisplay = formatPrice(trunc2(selectedBidOption?.total_price || selectedBidOption?.final_price));

  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), bg: '#F3F4F6', color: '#374151', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', bg: '#F3F4F6', color: '#374151', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', bg: '#DCFCE7', color: '#15803D', iconName: 'check' });
  }
  const badgesHtml = badges.map((b) =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  const amenities = aircraft?.aircraft_features?.custom_features || [];
  const amenitiesHtml = amenities.map((a) =>
    `<span style="display:inline-block;padding:4px 12px;background:#F3F4F6;color:#374151;font-size:12px;font-weight:500;border-radius:16px;margin-right:6px;margin-bottom:6px;">${icon('check', 14, '#374151')} ${a}</span>`
  ).join('');

  // ── Card brand badge ──────────────────────────────────────────────────────
  const cardBrandBadge = card?.brand?.toLowerCase() === 'visa'
    ? `<span style="display:inline-block;background-color:#1A1F71;color:#FFFFFF;font-size:9px;font-weight:900;padding:2px 5px;border-radius:3px;letter-spacing:0.5px;font-style:italic;vertical-align:middle;margin-right:6px;">VISA</span>`
    : card?.brand
      ? `<span style="display:inline-block;background-color:#252525;color:#FFFFFF;font-size:9px;font-weight:700;padding:2px 5px;border-radius:3px;vertical-align:middle;margin-right:6px;">${card?.brand.toUpperCase()}</span>`
      : '';

  const cardLast4Row = card?.last4
    ? `<td align="right" style="padding:4px 20px 14px;font-size:13px;color:#6B7280;white-space:nowrap;">${cardBrandBadge}•••• •••• •••• ${card?.last4}</td>`
    : '<td></td>';

  const orgStrong = organization?.name
    ? `<strong style="color:#111827;">${organization?.name}</strong>`
    : 'your broker';

  return `
${emailDocOpen({ title: 'Payment - Pre-Authorized' })}
${emailHeader({ title: 'Payment Pre-Authorized', organization })}

        ${spacer()}

        <!-- ===== CLIENT / REFERENCE ROW ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice For</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${clientName}</span>
                  </td>
                  ${quoteNumber ? `<td align="right">
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">TR Number</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${quoteNumber}</span>
                  </td>` : ''}
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== NOTIFICATION CARD ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:28px 28px 32px;">
              <table role="presentation" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
                <tr>
                  <td style="width:48px;height:48px;text-align:center;vertical-align:middle;background-color:#FEF3EB;border-radius:100%;">
                    ${icon('bill', 22)}
                  </td>
                </tr>
              </table>
              ${paymentMethod=='card' ? `
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Thank you for booking with ${orgStrong}. While we secure your aircraft, a temporary pre-authorization hold of <strong style="color:#111827;">USD ${fmt(totalAmount)}</strong> has been placed on your card${card?.last4 ? ` ending in <strong style="color:#111827;">${card?.last4}</strong>` : ''}.
              </p> 
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Please note that your card has not been charged. The funds are being temporarily reserved by your financial institution and will be captured only when the operator officially confirms the flight details.
              </p>` :  `
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Thank you for booking with ${orgStrong}. Because your preferred payment method is <strong>Wire/ACH</strong>, we've placed a tempporary authorization hold of <strong>USD ${fmt(cardAmount)}</strong> on your card ending ${card?.last4} as a payment guarantee while we secure your aircraft.
              </p>
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                The card authorization includes the <strong>3% credit card fee</strong>. This fee does not apply to your Wire/ACH payment. The total amount you need to send via Wire/ACH is <strong>USD ${fmt(totalAmount)}</strong>.
              </p>
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Please note that your card has not been charged. The funds are being temporarily reserved by your financial institution and will be captured only if your preferred payment does not clear and once the operator officially confirms the flight details.
              </p>
              `}

              ${paymentMethod=='card' ? `
              <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
                For further assistance, please reach out to ${orgStrong} or contact your broker directly.
              </p>` : ''}
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== PAYMENT DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td colspan="2" style="padding:14px 20px 10px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Payment Details</span>
            </td>
          </tr>

          <tr>
            <td style="padding:6px 20px 4px;font-size:13px;color:#6B7280;">Trip Cost</td>
            <td align="right" style="padding:6px 20px 4px;font-size:13px;color:#111827;">$${fmt(tripCost)}</td>
          </tr>
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Taxes &amp; Fees</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#111827;">$${fmt(taxesAndFees)}</td>
          </tr>

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>

          <tr> 
            <td style="padding:14px 20px 14px;font-size:15px;font-weight:700;color:#111827;">Total Due</td>
            <td align="right" style="padding:14px 20px 14px;font-size:18px;font-weight:700;color:#111827;">$${fmt(totalAmount)}</td>
          </tr>

          ${paymentMethod!='card' ? `
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Credit/Debit Card Fee (3%)</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#111827;">$${fmt(totalCardFee)}
            </td>
          </tr>
          ` : ``}

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>

          <tr>
            <td colspan="2" style="padding:10px 20px 4px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Payment Authorization By</span>
            </td>
          </tr>
          <tr>
            <td style="padding:4px 20px 14px;font-size:14px;font-weight:700;color:#111827;">${clientName}</td>
            ${cardLast4Row}
          </tr>

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>
          <tr>
            <td colspan="2" style="padding:12px 20px 14px;">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="vertical-align:middle;padding-right:10px;">
                    <div style="width:28px;height:28px;background-color:#F3F4F6;border-radius:6px;text-align:center;line-height:28px;">
                      ${icon('calendar', 14, '#6B7280')}
                    </div>
                  </td>
                  <td style="vertical-align:middle;">
                    <div style="font-size:11px;color:#6B7280;line-height:1.2;">Authorization Expiry</div>
                    <div style="font-size:13px;font-weight:600;color:#111827;line-height:1.4;">${expiryStr}</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== TRIP AUTH DISCLAIMER ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;">
          <tr>
            <td style="padding:0 20px;font-size:12px;color:#6B7280;line-height:1.6;">
              If your trip has succeeded, the authorization will turn into a real charge up to 24 hours before your trip and the amount will be deducted from your payment account.
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== TRIP DETAILS (matches paymentConfirmation layout) ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:20px 32px 8px;">
              <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Trip Details</span>
            </td>
          </tr>
          <tr>
            ${passengerCount ? `
            <td style="padding:12px 32px;" valign="top" width="50%">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('team', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">Passenger Count</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${passengerCount}</p>
                  </td>
                </tr>
              </table>
            </td>` : ''}
            <td style="padding:12px 32px;" valign="top" ${passengerCount ? 'width="50%"' : ''}>
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('plane', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">${tripType || 'Trip'}</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${routeDisplay}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ${departureDates.map((d, i) => `
          <tr>
            <td style="padding:12px 32px ${i === departureDates.length - 1 ? '20px' : '0'};" valign="top" ${passengerCount ? 'colspan="2"' : ''}>
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('calendar', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">${d.label}${departureDates.length > 1 ? ` (${d.route})` : ''}</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${d.date}${d.time ? ` at ${d.time}` : ''}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`).join('')}
        </table>

        ${spacer()}

        <!-- ===== AIRCRAFT CARD (matches bookingSecured layout) ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${imageUrl ? `
          <tr>
            <td style="padding:0;line-height:0;font-size:0;">
              <img src="${imageUrl}" alt="${modelName}" width="600" style="display:block;width:100%;height:auto;object-fit:cover;max-height:300px;" />
            </td>
          </tr>` : ''}
          <tr>
            <td style="padding:20px 32px;">
              <h2 style="margin:0 0 4px;font-size:20px;font-weight:500;color:#111827;">${aircraft?.tail_number || ''}</h2>
              <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>

              <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${priceDisplay}</span>
              </div>

              ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}

              ${aircraft?.description ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#6B7280;">About the Aircraft</h3>
                <p style="margin:0;font-size:13px;color:#171717;line-height:1.5;">${aircraft.description}</p>
              </div>` : ''}

              ${aircraft?.quote_notes ? `
              <div style="margin-top:16px;padding:12px 14px;background-color:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#92400E;">Aircraft Note</h3>
                <p style="margin:0;font-size:13px;color:#92400E;line-height:1.5;white-space:pre-wrap;">${aircraft.quote_notes}</p>
              </div>` : ''}

              ${amenitiesHtml ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 8px;font-size:13px;font-weight:600;color:#6B7280;">Amenities</h3>
                <div>${amenitiesHtml}</div>
              </div>` : ''}
            </td>
          </tr>
        </table>

        ${spacer()}

${emailFooter()}
${emailDocClose()}`;
};
